import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/api';
import './ApplicationDependencies.css';

interface BenefitRealizationRecord {
  // Primary Keys
  grouping_id: string;
  application_id: string;
  application_name?: string;
  group_name?: string;
  
  // Input/I-O Fields (user editable)
  disposition: 'RETIRE' | 'RE-PLATFORM' | 'RE-DESIGN' | 'RETAIN';
  total_annual_cost_current: number;
  total_disposition_cost: number;
  future_run_rate_annual: number;
  planned_project_cost: number;
  lease_buyback_benefit: number;
  months_horizon: number;
  
  // Calculated Output Fields (read-only)
  net_savings_value: number;
  net_savings_percent: number;
  adjusted_savings_value: number;
  adjusted_savings_percent: number;
  roi_percent: number;
  break_even_months_exact: number;
  
  // Audit fields
  created_at: string;
  updated_at: string;
  last_calculated_at: string;

  // Flags
  tco_missing?: boolean;

  // License Renegotiation fields
  license_renegotiation?: boolean;
  renegotiated_license_cost?: number;
  license_savings?: number;

  // Re-Platform sub-type flags
  consolidate?: boolean;
  replace_app?: boolean;
  consolidate_target_app?: string;
  replace_target_app?: string;

  // TCO Status
  tco_status?: string;
}

interface BenefitStatistics {
  totalApplications: number;
  totalTCO: number;
  totalMigrationCosts: number;
  totalAnnualSavings: number;
  avgBreakEvenMonths: number;
  totalOneYearSavings: number;
  totalTwoYearSavings: number;
  totalThreeYearSavings: number;
}

const BenefitRealization: React.FC = () => {
  const [records, setRecords] = useState<BenefitRealizationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [filterGroupId, setFilterGroupId] = useState('');
  const [filterApplicationId, setFilterApplicationId] = useState('');
  const [filterTCO, setFilterTCO] = useState<'all' | 'with' | 'without'>('all');
  const [filterTCOStatus, setFilterTCOStatus] = useState<string>('all');
  const [editingField, setEditingField] = useState<{record: BenefitRealizationRecord, field: string} | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipTimer, setTooltipTimer] = useState<NodeJS.Timeout | null>(null);
  const [showPlannedProjectTooltip, setShowPlannedProjectTooltip] = useState(false);
  const [plannedProjectTooltipTimer, setPlannedProjectTooltipTimer] = useState<NodeJS.Timeout | null>(null);
  const [stats, setStats] = useState<BenefitStatistics>({
    totalApplications: 0,
    totalTCO: 0,
    totalMigrationCosts: 0,
    totalAnnualSavings: 0,
    avgBreakEvenMonths: 0,
    totalOneYearSavings: 0,
    totalTwoYearSavings: 0,
    totalThreeYearSavings: 0
  });
  const [portfolioMetrics, setPortfolioMetrics] = useState<any>(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [approvedApplications, setApprovedApplications] = useState<Set<string>>(new Set());

  // Fetch approved applications from deployment status
  const fetchApprovedApplications = async () => {
    try {
      const response = await authFetch('/api/deployment-status');
      if (response.ok) {
        const data = await response.json();
        const approved = new Set<string>();
        data.forEach((item: any) => {
          if (item.deployment_status === 'Approved') {
            approved.add(item.application_id);
          }
        });
        setApprovedApplications(approved);
      }
    } catch (error) {
      console.error('Error fetching approved applications:', error);
    }
  };

  useEffect(() => {
    loadBenefitRealization();
    fetchApprovedApplications();
  }, []);

  useEffect(() => {
    calculateStatistics();
    loadPortfolioMetrics();
  }, [records]);

  useEffect(() => {
    // Component initialization
  }, []);


  // Helper to parse numeric values (PostgreSQL returns DECIMAL as strings)
  const parseStatNum = (value: number | string | undefined | null): number => {
    if (value === undefined || value === null) return 0;
    if (typeof value === 'number') return value;
    return parseFloat(value) || 0;
  };

  // Calculate 2-Year Savings inline for statistics
  const calcTwoYearSavingsForStats = (r: BenefitRealizationRecord): number => {
    const TCO = parseStatNum(r.total_annual_cost_current);
    const FRC = parseStatNum(r.future_run_rate_annual);
    const DC = parseStatNum(r.total_disposition_cost);
    const PPC = parseStatNum(r.planned_project_cost);
    const LB = parseStatNum(r.lease_buyback_benefit);
    return (TCO - FRC) * 2 - DC - PPC + LB;
  };

  const calculateStatistics = () => {
    const totalApps = records.length;
    const totalTCO = records.reduce((sum, r) => sum + parseStatNum(r.total_annual_cost_current), 0);
    const totalMigration = records.reduce((sum, r) => sum + parseStatNum(r.total_disposition_cost), 0);
    const totalSavings = records.reduce((sum, r) => sum + parseStatNum(r.net_savings_value), 0);
    const avgBreakEven = totalApps > 0
      ? records.reduce((sum, r) => sum + parseStatNum(r.break_even_months_exact), 0) / totalApps
      : 0;
    const calcNYearForStats = (r: BenefitRealizationRecord, n: number): number => {
      const TCO = parseStatNum(r.total_annual_cost_current);
      const FRC = parseStatNum(r.future_run_rate_annual);
      const DC = parseStatNum(r.total_disposition_cost);
      const PPC = parseStatNum(r.planned_project_cost);
      const LB = parseStatNum(r.lease_buyback_benefit);
      return (TCO - FRC) * n - DC - PPC + LB;
    };
    const totalOneYear = records.reduce((sum, r) => sum + calcNYearForStats(r, 1), 0);
    const totalTwoYear = records.reduce((sum, r) => sum + calcNYearForStats(r, 2), 0);
    const totalThreeYear = records.reduce((sum, r) => sum + calcNYearForStats(r, 3), 0);

    setStats({
      totalApplications: totalApps,
      totalTCO: totalTCO,
      totalMigrationCosts: totalMigration,
      totalAnnualSavings: totalSavings,
      avgBreakEvenMonths: avgBreakEven,
      totalOneYearSavings: totalOneYear,
      totalTwoYearSavings: totalTwoYear,
      totalThreeYearSavings: totalThreeYear
    });
  };

  const loadPortfolioMetrics = async () => {
    try {
      const response = await authFetch('/api/benefit-realization/portfolio-metrics');
      if (response.ok) {
        const data = await response.json();
        setPortfolioMetrics(data.portfolioMetrics);
      }
    } catch (err) {
      console.warn('Failed to load portfolio metrics:', err);
    }
  };

  const loadBenefitRealization = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/benefit-realization');

      // Don't treat 404 as an error - it just means no data
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch benefit realization data');
      }

      const data = response.ok ? await response.json() : [];
      setRecords(Array.isArray(data) ? data : []);
      setError('');
    } catch (err: any) {
      console.error('Error loading benefit realization data:', err);
      setRecords([]);
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const initializeFromDispositionAnalysis = async () => {
    try {
      const response = await authFetch('/api/benefit-realization/initialize-from-disposition-analysis', {
        method: 'POST'
      });

      // Don't treat 404 as an error - it just means no disposition data exists
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to initialize benefit realization data');
      }

      if (response.ok) {
        const result = await response.json();
        // Reload the data
        await loadBenefitRealization();
      } else {
        // No disposition data to initialize from, just set empty records
        setRecords([]);
        setError('');
      }
    } catch (err: any) {
      console.error('Error initializing benefit realization data:', err);
      setRecords([]);
      setError('');
    }
  };

  const updateRecord = async (record: BenefitRealizationRecord, field: string, value: any) => {
    try {
      const updateData = { [field]: value };

      const response = await authFetch(`/api/benefit-realization/${record.grouping_id}/${record.application_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update record: ${response.status} ${errorText}`);
      }

      const responseData = await response.json();

      // Extract the updated record and portfolio metrics
      const { portfolioMetrics: updatedPortfolioMetrics, ...updatedRecord } = responseData;

      // Update the local state
      setRecords(prev => prev.map(r =>
        r.grouping_id === record.grouping_id && r.application_id === record.application_id
          ? updatedRecord
          : r
      ));

      // Update portfolio metrics if included in response
      if (updatedPortfolioMetrics) {
        setPortfolioMetrics(updatedPortfolioMetrics);
      }

    } catch (err: any) {
      setError(err.message || 'Failed to update record');
    }
  };

  const recalculateRecord = async (record: BenefitRealizationRecord) => {
    try {
      console.log(`🔄 Recalculating ${record.grouping_id}/${record.application_id}`);

      const response = await authFetch(`/api/benefit-realization/recalculate/${record.grouping_id}/${record.application_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to recalculate: ${response.status} ${errorText}`);
      }

      const updatedRecord = await response.json();

      setRecords(prev => prev.map(r =>
        r.grouping_id === record.grouping_id && r.application_id === record.application_id
          ? updatedRecord : r
      ));
      setUpdateTrigger(prev => prev + 1);

    } catch (err: any) {
      console.error('Recalculation error:', err);
      setError(err.message || 'Failed to recalculate record');
    }
  };

  const recalculateAll = async () => {
    try {
      console.log('🔄🔄 Frontend: Starting recalculate all');
      setLoading(true);
      setError('');
      
      const response = await authFetch('/api/benefit-realization/recalculate-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to recalculate all records: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Frontend: Received recalculate all result:', result);
      
      if (result.records && result.records.length > 0) {
        console.log(`🔄 Frontend: Updating state with ${result.records.length} records`);
        setRecords([...result.records]); // Force new array reference
        setUpdateTrigger(prev => prev + 1); // Force component re-render
        
        // Update portfolio metrics if included in response
        if (result.portfolioMetrics) {
          setPortfolioMetrics(result.portfolioMetrics);
        }
      } else {
        console.log('🔄 Frontend: No records in result, reloading from API');
        await loadBenefitRealization();
      }
      
      if (result.errors && result.errors.length > 0) {
        console.warn('⚠️ Frontend: Errors in recalculate result:', result.errors);
      }
      
    } catch (err: any) {
      console.error('🚨 Frontend recalculate all error:', err);
      setError(err.message || 'Failed to recalculate all records');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = (record: BenefitRealizationRecord, field: string) => {
    setEditingField({ record, field });
    setEditValue(String(record[field as keyof BenefitRealizationRecord]));
  };

  const handleEditSave = async () => {
    if (!editingField) return;

    const { record, field } = editingField;
    const numericValue = parseFloat(editValue) || 0;
    
    await updateRecord(record, field, numericValue);
    setEditingField(null);
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const filteredRecords = records.filter(record => {
    if (!filterGroupId) return false;
    const matchesGroup = filterGroupId === '__ALL__' ||
      record.grouping_id === filterGroupId;
    const matchesApp = !filterApplicationId || record.application_id === filterApplicationId;
    const tco = parseFloat(String(record.total_annual_cost_current)) || 0;
    const matchesTCO = filterTCO === 'all' ||
      (filterTCO === 'with' && tco > 0) ||
      (filterTCO === 'without' && tco === 0);
    const matchesTCOStatus = filterTCOStatus === 'all' || (record.tco_status || 'Not Defined') === filterTCOStatus;
    return matchesGroup && matchesApp && matchesTCO && matchesTCOStatus;
  });

  const displayStats: BenefitStatistics = (() => {
    const source = filterGroupId ? filteredRecords : records;
    const totalApps = source.length;
    const totalTCO = source.reduce((sum, r) => sum + (parseFloat(String(r.total_annual_cost_current)) || 0), 0);
    const totalMigration = source.reduce((sum, r) => sum + (parseFloat(String(r.total_disposition_cost)) || 0), 0);
    const totalSavings = source.reduce((sum, r) => sum + (parseFloat(String(r.net_savings_value)) || 0), 0);
    const calcN = (r: BenefitRealizationRecord, n: number): number => {
      if (r.license_renegotiation && (parseFloat(String(r.renegotiated_license_cost)) || 0) > 0) {
        return (parseFloat(String(r.license_savings)) || 0) * n
          - (parseFloat(String(r.planned_project_cost)) || 0)
          - (parseFloat(String(r.total_disposition_cost)) || 0)
          + (parseFloat(String(r.lease_buyback_benefit)) || 0);
      }
      const TCO = parseFloat(String(r.total_annual_cost_current)) || 0;
      const FRC = parseFloat(String(r.future_run_rate_annual)) || 0;
      const DC = parseFloat(String(r.total_disposition_cost)) || 0;
      const PPC = parseFloat(String(r.planned_project_cost)) || 0;
      const LB = parseFloat(String(r.lease_buyback_benefit)) || 0;
      return (TCO - FRC) * n - DC - PPC + LB;
    };
    const totalAnnualSavings = source.reduce((sum, r) => {
      const TCO = parseFloat(String(r.total_annual_cost_current)) || 0;
      const FRC = parseFloat(String(r.future_run_rate_annual)) || 0;
      return sum + (TCO - FRC);
    }, 0);
    const totalNetInvestment = source.reduce((sum, r) => {
      const DC = parseFloat(String(r.total_disposition_cost)) || 0;
      const PPC = parseFloat(String(r.planned_project_cost)) || 0;
      const LB = parseFloat(String(r.lease_buyback_benefit)) || 0;
      return sum + (DC + PPC - LB);
    }, 0);
    const portfolioBreakEvenMonths = totalAnnualSavings > 0
      ? (totalNetInvestment / totalAnnualSavings) * 12
      : 0;
    return {
      totalApplications: totalApps,
      totalTCO,
      totalMigrationCosts: totalMigration,
      totalAnnualSavings: totalSavings,
      avgBreakEvenMonths: portfolioBreakEvenMonths,
      totalOneYearSavings: source.reduce((sum, r) => sum + calcN(r, 1), 0),
      totalTwoYearSavings: source.reduce((sum, r) => sum + calcN(r, 2), 0),
      totalThreeYearSavings: source.reduce((sum, r) => sum + calcN(r, 3), 0),
    };
  })();

  const renderEditableCell = (record: BenefitRealizationRecord, field: keyof BenefitRealizationRecord, value: number) => {
    const isEditing = editingField?.record.grouping_id === record.grouping_id && 
                     editingField?.record.application_id === record.application_id && 
                     editingField?.field === field;
    
    // Check if field should be non-editable for specific dispositions
    const isDispositionCostLocked = field === 'total_disposition_cost'; // Always read-only — sourced from Disposition Cost Analysis
    const isNonEditableForRetire = field === 'future_run_rate_annual' && record.disposition === 'RETIRE';
    const isNonEditable = isDispositionCostLocked || isNonEditableForRetire;
    
    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <input
            type="text"
            inputMode="decimal"
            className="no-spinners"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            style={{
              width: '130px',
              padding: '2px 4px',
              border: '1px solid #007acc',
              borderRadius: '3px',
              fontSize: '14px',
              MozAppearance: 'textfield',
              WebkitAppearance: 'none'
            } as React.CSSProperties}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleEditSave();
              if (e.key === 'Escape') handleEditCancel();
            }}
          />
          <button onClick={handleEditSave} style={{ padding: '1px 4px', fontSize: '10px' }}>✓</button>
          <button onClick={handleEditCancel} style={{ padding: '1px 4px', fontSize: '10px' }}>✕</button>
        </div>
      );
    }

    return (
      <div
        onClick={() => !isNonEditable && handleEditStart(record, field)}
        style={{
          cursor: isNonEditable ? 'not-allowed' : 'pointer',
          padding: '2px 4px',
          borderRadius: '3px',
          backgroundColor: isNonEditable ? '#f5f5f5' : 'transparent',
          color: isNonEditable ? '#999' : 'inherit'
        }}
        title={
          isDispositionCostLocked ? 'Disposition Cost is read-only — sourced from Disposition Cost Analysis' :
          isNonEditableForRetire ? 'Future Run Rate must be 0 for RETIRE disposition' :
          'Click to edit'
        }
        onMouseEnter={(e) => {
          if (!isNonEditable) {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
          }
        }}
        onMouseLeave={(e) => {
          if (!isNonEditable) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        {formatCurrency(value)}
      </div>
    );
  };

  const exportToCSV = () => {
    // Use all records if no filter is applied, otherwise use filtered records
    const recordsToExport = filterGroupId ? filteredRecords : records;

    if (recordsToExport.length === 0) {
      alert('No data to download');
      return;
    }

    const headers = [
      'Group ID', 'Group Name', 'Application ID', 'Application Name', 'Disposition',
      'Total Annual Cost', 'Total Disposition Cost', 'Future Run Rate Costs', 'Planned Project Costs', 'Net Savings', 'Net Savings %', 'Lease Buyback', 'Renegotiated License Cost', 'License Savings', 'Adjusted Savings', 'Adjusted %',
      'Break Even (Months)', '1-Year Savings', 'ROI % (over 1 year)', '2-Year Savings', 'ROI % (over 2 years)', '3-Year Savings', 'ROI % (over 3 years)'
    ];

    const rows = recordsToExport.map(record => [
      record.grouping_id,
      record.group_name || '',
      record.application_id,
      record.application_name || '',
      record.disposition,
      parseNum(record.total_annual_cost_current),
      parseNum(record.total_disposition_cost),
      parseNum(record.future_run_rate_annual),
      parseNum(record.planned_project_cost),
      parseNum(record.net_savings_value),
      parseNum(record.net_savings_percent),
      parseNum(record.lease_buyback_benefit),
      record.license_renegotiation ? parseNum(record.renegotiated_license_cost) : 0,
      record.license_renegotiation ? parseNum(record.license_savings) : 0,
      calculateAdjustedSavingsValue(record),
      parseNum(record.adjusted_savings_percent),
      parseNum(record.break_even_months_exact),
      calculateOneYearSavings(record),
      calculateNYearROI(record, 1),
      calculateTwoYearSavings(record),
      calculateNYearROI(record, 2),
      calculateThreeYearSavings(record),
      calculateNYearROI(record, 3)
    ]);

    // Calculate Grand Total row
    const totalCount = recordsToExport.length;
    const grandTotal = [
      'GRAND TOTAL',
      `${totalCount} Applications`,
      '',
      '',
      '',
      recordsToExport.reduce((sum, r) => sum + parseNum(r.total_annual_cost_current), 0),
      recordsToExport.reduce((sum, r) => sum + parseNum(r.total_disposition_cost), 0),
      recordsToExport.reduce((sum, r) => sum + parseNum(r.future_run_rate_annual), 0),
      recordsToExport.reduce((sum, r) => sum + parseNum(r.planned_project_cost), 0),
      recordsToExport.reduce((sum, r) => sum + parseNum(r.net_savings_value), 0),
      '', // Net Savings % - not summable
      recordsToExport.reduce((sum, r) => sum + parseNum(r.lease_buyback_benefit), 0),
      recordsToExport.filter(r => r.license_renegotiation).reduce((sum, r) => sum + parseNum(r.renegotiated_license_cost), 0),
      recordsToExport.filter(r => r.license_renegotiation).reduce((sum, r) => sum + parseNum(r.license_savings), 0),
      recordsToExport.reduce((sum, r) => sum + calculateAdjustedSavingsValue(r), 0),
      '', // Adjusted % - not summable
      totalCount > 0 ? (recordsToExport.reduce((sum, r) => sum + parseNum(r.break_even_months_exact), 0) / totalCount).toFixed(1) : 0, // Average break-even
      recordsToExport.reduce((sum, r) => sum + calculateOneYearSavings(r), 0),
      '', // ROI % (1yr) - not summable
      recordsToExport.reduce((sum, r) => sum + calculateTwoYearSavings(r), 0),
      '', // ROI % (2yr) - not summable
      recordsToExport.reduce((sum, r) => sum + calculateThreeYearSavings(r), 0),
      '' // ROI % (3yr) - not summable
    ];

    // RFC 4180 CSV cell escaping — quote any cell containing a comma, quote, or newline,
    // and double-up any embedded quotes. Without this, application names like
    // "ServiceNow - ITSM, myIT" shift subsequent columns and corrupt downstream sums.
    const csvEscape = (cell: unknown): string => {
      const s = cell === null || cell === undefined ? '' : String(cell);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const toRow = (cells: unknown[]) => cells.map(csvEscape).join(',');

    const csvContent = [
      toRow(headers),
      ...rows.map(toRow),
      '', // Empty line before grand total
      toRow(grandTotal)
    ].join('\n');

    // UTF-8 BOM helps Excel detect encoding and preserve quoted-field parsing on Windows.
    const blob = new Blob(['﻿', csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'benefit-realization-analysis.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Helper to parse numeric values (PostgreSQL returns DECIMAL as strings)
  const parseNum = (value: number | string | undefined | null): number => {
    if (value === undefined || value === null) return 0;
    if (typeof value === 'number') return value;
    return parseFloat(value) || 0;
  };

  // Utility functions
  const calculateAdjustedSavingsValue = (record: BenefitRealizationRecord): number => {
    return parseNum(record.net_savings_value) + parseNum(record.lease_buyback_benefit);
  };

  // Calculate N-Year Savings using formula: (TCO - FRC) * N - DC - PPC + LB
  const calculateNYearSavings = (record: BenefitRealizationRecord, years: number): number => {
    // For renegotiation apps: savings = license_savings × years - PPC - DC + LB
    if (record.license_renegotiation && parseNum(record.renegotiated_license_cost) > 0) {
      return parseNum(record.license_savings) * years
        - parseNum(record.planned_project_cost)
        - parseNum(record.total_disposition_cost)
        + parseNum(record.lease_buyback_benefit);
    }
    const TCO = parseNum(record.total_annual_cost_current);
    const FRC = parseNum(record.future_run_rate_annual);
    const DC = parseNum(record.total_disposition_cost);
    const PPC = parseNum(record.planned_project_cost);
    const LB = parseNum(record.lease_buyback_benefit);

    return (TCO - FRC) * years - DC - PPC + LB;
  };

  const calculateOneYearSavings = (record: BenefitRealizationRecord): number => calculateNYearSavings(record, 1);
  const calculateTwoYearSavings = (record: BenefitRealizationRecord): number => calculateNYearSavings(record, 2);
  const calculateThreeYearSavings = (record: BenefitRealizationRecord): number => calculateNYearSavings(record, 3);

  // Calculate ROI % over N years
  const calculateNYearROI = (record: BenefitRealizationRecord, years: number): number => {
    // For renegotiation apps: ROI = (N-year savings / TCO) × 100
    if (record.license_renegotiation && parseNum(record.renegotiated_license_cost) > 0) {
      const nYearSavings = calculateNYearSavings(record, years);
      const tco = parseNum(record.total_annual_cost_current);
      return tco > 0 ? (nYearSavings / tco) * 100 : 0;
    }
    const savings = calculateNYearSavings(record, years);
    const totalCosts = parseNum(record.total_disposition_cost) + parseNum(record.planned_project_cost);
    return totalCosts > 0 ? (savings / totalCosts) * 100 : 0;
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'RETIRE': return '#dc2626';
      case 'RE-PLATFORM': return '#f59e0b';
      case 'RE-DESIGN': return '#3b82f6';
      case 'RETAIN': return '#10b981';
      default: return '#6c757d';
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatPercentage = (value: number): string => {
    const percentValue = (value || 0) * 100;
    // Truncate to 2 decimal places without rounding
    const truncated = Math.floor(percentValue * 100) / 100;
    const formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(truncated);
    return `${formattedValue}%`;
  };

  const formatROIPercentage = (value: number): string => {
    // ROI is already stored as percentage in database (e.g., 386.22 for 386.22%)
    // Truncate to 2 decimal places without rounding
    const truncated = Math.floor((value || 0) * 100) / 100;
    const formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(truncated);
    return `${formattedValue}%`;
  };
  
  const formatPortfolioROI = (value: number): string => {
    // Portfolio ROI comes as decimal (e.g., 2.7020 for 270.20%)
    const percentValue = (value || 0) * 100;
    const formattedValue = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(percentValue);
    return `${formattedValue}%`;
  };

  const formatMonths = (value: number | string): string => {
    if (value === null || value === undefined) return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'N/A';
    if (numValue <= 0) return 'Immediate'; // Negative or zero means immediate payback
    return `${numValue.toFixed(1)} months`;
  };

  if (loading) {
    return (
      <div style={{ padding: '20px' }}>
        <h2>Benefit Realization Analysis</h2>
        <div className="md-empty-state">
          <div className="md-empty-icon">⏳</div>
          <h3>Loading Benefit Realization Data</h3>
          <p>Please wait while we fetch the data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <h2>Benefit Realization Analysis</h2>
        <div style={{ color: '#dc2626', padding: '10px', border: '1px solid #dc2626', borderRadius: '4px' }}>
          Error: {error}
          <button 
            onClick={loadBenefitRealization} 
            style={{ marginLeft: '10px', padding: '5px 10px' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page page-fixed-header">
      {/* Fixed Header Section - Does not scroll */}
      <div className="fixed-header-section">
        {/* Header */}
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Benefit Realization Analysis</h1>
            </div>
            <div className="page-header__actions">
              <button
                onClick={() => recalculateAll()}
                disabled={loading || records.length === 0}
                className="btn btn-primary"
                title="Recalculate all benefit analysis values"
              >
                {loading ? 'Recalculating...' : '♻ Recalculate All'}
              </button>
              {records.length > 0 && (
                <button
                  onClick={exportToCSV}
                  className="btn btn-outline-success"
                  title="Download current data as CSV"
                  style={{ border: '2px solid #28a745' }}
                >
                  📥 Download Data
                </button>
              )}
            </div>
          </div>
        </div>

      {/* Statistics Cards - Only show when a grouping is selected */}
      {filterGroupId && records.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'nowrap' }}>
          <div style={{ flex: '1', padding: '8px 10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', borderLeft: '3px solid #007acc', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ margin: '0 0 2px 0', fontSize: '10px', color: '#6c757d', fontWeight: '500', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Total Applications</div>
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#007acc', whiteSpace: 'nowrap' }}>
              {displayStats.totalApplications}
            </div>
          </div>
          <div style={{ flex: '1', padding: '8px 10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', borderLeft: '3px solid #6c757d', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ margin: '0 0 2px 0', fontSize: '10px', color: '#6c757d', fontWeight: '500', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>TCO</div>
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#495057', whiteSpace: 'nowrap' }}>
              {formatCurrency(displayStats.totalTCO)}
            </div>
          </div>
          <div style={{ flex: '1', padding: '8px 10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', borderLeft: '3px solid #28a745', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ margin: '0 0 2px 0', fontSize: '10px', color: '#6c757d', fontWeight: '500', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Annual Savings</div>
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#28a745', whiteSpace: 'nowrap' }}>
              {formatCurrency(displayStats.totalAnnualSavings)}
            </div>
          </div>
          <div style={{ flex: '1', padding: '8px 10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', borderLeft: '3px solid #007acc', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ margin: '0 0 2px 0', fontSize: '10px', color: '#6c757d', fontWeight: '500', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Break-Even</div>
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#007acc', whiteSpace: 'nowrap' }}>
              {formatMonths(displayStats.avgBreakEvenMonths)}
            </div>
          </div>
          <div style={{ flex: '1', padding: '8px 10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', borderLeft: '3px solid #28a745', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ margin: '0 0 2px 0', fontSize: '10px', color: '#6c757d', fontWeight: '500', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>1-Year Savings</div>
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#28a745', whiteSpace: 'nowrap' }}>
              {formatCurrency(displayStats.totalOneYearSavings)}
            </div>
          </div>
          <div style={{ flex: '1', padding: '8px 10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', borderLeft: '3px solid #28a745', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ margin: '0 0 2px 0', fontSize: '10px', color: '#6c757d', fontWeight: '500', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>2-Year Savings</div>
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#28a745', whiteSpace: 'nowrap' }}>
              {formatCurrency(displayStats.totalTwoYearSavings)}
            </div>
          </div>
          <div style={{ flex: '1', padding: '8px 10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', borderLeft: '3px solid #28a745', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ margin: '0 0 2px 0', fontSize: '10px', color: '#6c757d', fontWeight: '500', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>3-Year Savings</div>
            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#28a745', whiteSpace: 'nowrap' }}>
              {formatCurrency(displayStats.totalThreeYearSavings)}
            </div>
          </div>
          {/* License Renegotiation Savings card */}
          {(() => {
            const source = filterGroupId ? filteredRecords : records;
            const renegoApps = source.filter(r => r.license_renegotiation);
            const totalLicenseSavings = renegoApps.reduce((sum, r) => sum + (parseFloat(String(r.license_savings)) || 0), 0);
            return renegoApps.length > 0 ? (
              <div style={{ flex: '1', padding: '8px 10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', borderLeft: '3px solid #e65100', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ margin: '0 0 2px 0', fontSize: '10px', color: '#6c757d', fontWeight: '500', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.3px' }}>License Renegotiation Savings</div>
                <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#e65100', whiteSpace: 'nowrap' }}>
                  {formatCurrency(totalLicenseSavings)}
                </div>
                <div style={{ fontSize: '10px', color: '#999', marginTop: '1px' }}>{renegoApps.length} app{renegoApps.length > 1 ? 's' : ''}</div>
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* Filters */}
      {records.length > 0 && <div className="md-card" style={{ marginBottom: '20px' }}>
        <div className="md-card-content" style={{ padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', overflowX: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                Grouping ID:
              </label>
              <select
                value={filterGroupId}
                onChange={(e) => {
                  setFilterGroupId(e.target.value);
                  setFilterApplicationId(''); // Reset Application ID when Grouping changes
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  minWidth: '180px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select Grouping</option>
                <option value="__ALL__">All Grouping IDs</option>
                {Array.from(new Set(records.map(r => r.grouping_id).filter(Boolean))).sort().map(groupId => {
                  const groupName = records.find(r => r.grouping_id === groupId)?.group_name;
                  return <option key={groupId} value={groupId}>{groupId}{groupName ? ` — ${groupName}` : ''}</option>;
                })}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', whiteSpace: 'nowrap' }}>
                Application:
              </label>
              <select
                value={filterApplicationId}
                onChange={(e) => setFilterApplicationId(e.target.value)}
                disabled={!filterGroupId}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  minWidth: '140px',
                  maxWidth: '260px',
                  backgroundColor: !filterGroupId ? '#f5f5f5' : 'white',
                  cursor: !filterGroupId ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="">All Application IDs</option>
                {Array.from(new Map(records
                  .filter(r => !filterGroupId || filterGroupId === '__ALL__' || r.grouping_id === filterGroupId)
                  .filter(r => r.application_id)
                  .map(r => [r.application_id, r.application_name || ''])
                )).sort((a, b) => a[0].localeCompare(b[0])).map(([appId, appName]) => (
                  <option key={appId} value={appId}>{appId} - {appName}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', whiteSpace: 'nowrap' }}>
                TCO Data:
              </label>
              <select
                value={filterTCO}
                onChange={(e) => { setFilterTCO(e.target.value as 'all' | 'with' | 'without'); setFilterTCOStatus('all'); }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  minWidth: '130px',
                  backgroundColor: 'white'
                }}
              >
                <option value="all">All Apps</option>
                <option value="with">With TCO only</option>
                <option value="without">Without TCO only</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', whiteSpace: 'nowrap' }}>
                TCO Status:
              </label>
              <select
                value={filterTCOStatus}
                onChange={(e) => setFilterTCOStatus(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  minWidth: '130px',
                  backgroundColor: 'white'
                }}
              >
                <option value="all">All Statuses</option>
                {filterTCO === 'without' ? (
                  <option value="Not Defined">Not Defined</option>
                ) : filterTCO === 'with' ? (
                  <>
                    <option value="Defined">Defined</option>
                    <option value="Bundled">Bundled</option>
                    <option value="Free">Free / Open Source</option>
                  </>
                ) : (
                  <>
                    <option value="Defined">Defined</option>
                    <option value="Bundled">Bundled</option>
                    <option value="Free">Free / Open Source</option>
                    <option value="Not Defined">Not Defined</option>
                  </>
                )}
              </select>
            </div>
            {(filterGroupId || filterApplicationId || filterTCO !== 'all' || filterTCOStatus !== 'all') && (
              <button
                onClick={() => {
                  setFilterGroupId('');
                  setFilterApplicationId('');
                  setFilterTCO('all');
                  setFilterTCOStatus('all');
                }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}
              >
                Clear Filters
              </button>
            )}
            {filterGroupId && (
              <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: 'auto' }}>
                Showing {filteredRecords.length} of {records.length} records
              </span>
            )}
          </div>
        </div>
      </div>}
      </div>{/* End fixed-header-section */}

      {/* Scrollable Data Section */}
      <div className="scrollable-data-section" style={{ overflowX: 'auto' }}>

      {records.length === 0 && !loading && (
        <div className="md-empty-state">
          <div className="md-empty-icon">💰</div>
          <h3>No Benefit Realization Data Available</h3>
          <p>Benefit realization records will appear here once disposition cost analysis data has been created.</p>
        </div>
      )}

      {/* Main Data Table */}
      {records.length > 0 && <div style={{ overflowX: 'auto', border: '1px solid #dee2e6', borderRadius: '8px', paddingBottom: '14px' }}>
        {filteredRecords.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1800px', fontSize: '15px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ position: 'sticky', left: 0, backgroundColor: '#e9ecef', padding: '8px', borderRight: '2px solid #dee2e6', minWidth: '150px', textAlign: 'left', zIndex: 5 }}>Grouping ID</th>
                <th style={{ position: 'sticky', left: '150px', backgroundColor: '#e9ecef', padding: '8px', borderRight: '2px solid #dee2e6', minWidth: '150px', textAlign: 'left', zIndex: 4 }}>Application ID</th>
                <th style={{ position: 'sticky', left: '300px', backgroundColor: '#e9ecef', padding: '8px', borderRight: '2px solid #dee2e6', borderBottom: '1px solid #dee2e6', textAlign: 'center', minWidth: '140px', zIndex: 3 }}>Disposition</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', minWidth: '170px' }}>Total Annual Cost</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', minWidth: '170px' }}>Disposition Cost</th>
                <th 
                  style={{ 
                    padding: '8px', 
                    borderBottom: '1px solid #dee2e6', 
                    textAlign: 'right', 
                    minWidth: '170px',
                    position: 'relative',
                    cursor: 'help'
                  }}
                  onMouseEnter={() => {
                    if (tooltipTimer) clearTimeout(tooltipTimer);
                    const timer = setTimeout(() => {
                      setShowTooltip(true);
                    }, 2000);
                    setTooltipTimer(timer);
                  }}
                  onMouseLeave={() => {
                    if (tooltipTimer) clearTimeout(tooltipTimer);
                    setShowTooltip(false);
                    setTooltipTimer(null);
                  }}
                >
                  Future Run Rate Costs (Annual)
                </th>
                <th
                  style={{
                    padding: '8px',
                    borderBottom: '1px solid #dee2e6',
                    textAlign: 'right',
                    minWidth: '170px',
                    position: 'relative',
                    cursor: 'help'
                  }}
                  onMouseEnter={() => {
                    if (plannedProjectTooltipTimer) clearTimeout(plannedProjectTooltipTimer);
                    const timer = setTimeout(() => {
                      setShowPlannedProjectTooltip(true);
                    }, 2000);
                    setPlannedProjectTooltipTimer(timer);
                  }}
                  onMouseLeave={() => {
                    if (plannedProjectTooltipTimer) clearTimeout(plannedProjectTooltipTimer);
                    setShowPlannedProjectTooltip(false);
                    setPlannedProjectTooltipTimer(null);
                  }}
                >
                  Planned Project Costs (One-Time)
                </th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', minWidth: '170px' }}>Net Savings</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center', minWidth: '120px' }}>Net Savings %</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', minWidth: '170px' }}>Lease Buyback</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', minWidth: '180px', backgroundColor: '#fff3e0' }}>Renegotiated License Cost</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', minWidth: '150px', backgroundColor: '#fff3e0' }}>License Savings</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', minWidth: '170px' }}>Adjusted Savings</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center', minWidth: '120px' }}>Adjusted %</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center', minWidth: '110px' }}>Break Even</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', minWidth: '170px' }}>1-Year Savings</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center', minWidth: '120px' }}>ROI % (over 1 year)</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', minWidth: '170px' }}>2-Year Savings</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center', minWidth: '120px' }}>ROI % (over 2 years)</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', minWidth: '170px' }}>3-Year Savings</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center', minWidth: '120px' }}>ROI % (over 3 years)</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'center', minWidth: '80px' }}>Recalc</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((record) => (
                <tr key={`${record.grouping_id}-${record.application_id}`}>
                  <td style={{
                    position: 'sticky',
                    left: 0,
                    padding: '6px',
                    backgroundColor: '#fff',
                    borderRight: '2px solid #dee2e6',
                    minWidth: '150px',
                    textAlign: 'left',
                    zIndex: 5
                  }}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{record.grouping_id}</div>
                      {record.group_name && (
                        <div style={{ fontSize: '10px', color: '#666' }}>{record.group_name}</div>
                      )}
                    </div>
                  </td>
                  <td style={{
                    position: 'sticky',
                    left: '150px',
                    padding: '6px',
                    backgroundColor: '#fff',
                    borderRight: '2px solid #dee2e6',
                    minWidth: '150px',
                    textAlign: 'left',
                    zIndex: 4
                  }}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{record.application_id}</div>
                      {record.application_name && (
                        <div style={{ fontSize: '10px', color: '#666' }}>{record.application_name}</div>
                      )}
                      {record.tco_status && record.tco_status !== 'Defined' && (
                        <span style={{
                          padding: '1px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: '600',
                          backgroundColor: record.tco_status === 'Bundled' ? '#BDD7EE' : record.tco_status === 'Free' ? '#E2EFDA' : '#FFC7CE',
                          color: record.tco_status === 'Bundled' ? '#1565c0' : record.tco_status === 'Free' ? '#2e7d32' : '#9C0006'
                        }}>
                          {record.tco_status === 'Not Defined' ? 'TCO Not Defined' : record.tco_status}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{
                    position: 'sticky',
                    left: '300px',
                    backgroundColor: '#fff',
                    borderRight: '2px solid #dee2e6',
                    textAlign: 'center',
                    padding: '6px',
                    zIndex: 3,
                    minWidth: '140px'
                  }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: getRecommendationColor(record.disposition),
                      color: 'white',
                      textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }}>
                      {record.disposition}
                      {record.disposition === 'RE-PLATFORM' && record.consolidate && ' (Consolidate)'}
                      {record.disposition === 'RE-PLATFORM' && record.replace_app && ' (Replace)'}
                    </span>
                  </td>
                  <td style={{
                    textAlign: 'right', padding: '6px',
                    ...(record.tco_missing ? { backgroundColor: '#fee2e2', color: '#dc2626', fontWeight: 700 } : {})
                  }}>
                    {record.tco_missing
                      ? <span title="TCO is missing - no current run rate data defined">$0.00 (Missing)</span>
                      : formatCurrency(record.total_annual_cost_current)
                    }
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px' }}>
                    {renderEditableCell(record, 'total_disposition_cost', record.total_disposition_cost)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px' }}>
                    {renderEditableCell(record, 'future_run_rate_annual', record.future_run_rate_annual)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px' }}>
                    {renderEditableCell(record, 'planned_project_cost', record.planned_project_cost)}
                  </td>
                  <td style={{ 
                    textAlign: 'right', 
                    padding: '6px',
                    backgroundColor: record.net_savings_value > 0 ? '#f0fdf4' : record.net_savings_value < 0 ? '#fef2f2' : '#fff',
                    color: record.net_savings_value > 0 ? '#166534' : record.net_savings_value < 0 ? '#dc2626' : '#000'
                  }}>
                    {formatCurrency(record.net_savings_value)}
                  </td>
                  <td style={{ 
                    textAlign: 'center', 
                    padding: '6px',
                    backgroundColor: record.net_savings_percent > 0 ? '#f0fdf4' : record.net_savings_percent < 0 ? '#fef2f2' : '#fff',
                    color: record.net_savings_percent > 0 ? '#166534' : record.net_savings_percent < 0 ? '#dc2626' : '#000'
                  }}>
                    {formatPercentage(record.net_savings_percent)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px' }}>
                    {renderEditableCell(record, 'lease_buyback_benefit', record.lease_buyback_benefit)}
                  </td>
                  {/* Renegotiated License Cost — editable only for RETAIN + renegotiation flagged */}
                  <td style={{ textAlign: 'right', padding: '6px', backgroundColor: record.license_renegotiation ? '#fff3e0' : '#f9f9f9' }}>
                    {record.license_renegotiation && record.disposition === 'RETAIN'
                      ? renderEditableCell(record, 'renegotiated_license_cost' as keyof BenefitRealizationRecord, record.renegotiated_license_cost || 0)
                      : <span style={{ color: '#ccc', fontSize: '12px' }}>N/A</span>
                    }
                  </td>
                  {/* License Savings — calculated, read-only */}
                  <td style={{
                    textAlign: 'right', padding: '6px',
                    backgroundColor: record.license_renegotiation ? '#fff3e0' : '#f9f9f9',
                    color: (record.license_savings || 0) > 0 ? '#166534' : '#000',
                    fontWeight: record.license_renegotiation ? 600 : 400
                  }}>
                    {record.license_renegotiation
                      ? formatCurrency(record.license_savings || 0)
                      : <span style={{ color: '#ccc', fontSize: '12px' }}>N/A</span>
                    }
                  </td>
                  <td style={{
                    textAlign: 'right',
                    padding: '6px',
                    backgroundColor: calculateAdjustedSavingsValue(record) > 0 ? '#f0fdf4' : calculateAdjustedSavingsValue(record) < 0 ? '#fef2f2' : '#fff',
                    color: calculateAdjustedSavingsValue(record) > 0 ? '#166534' : calculateAdjustedSavingsValue(record) < 0 ? '#dc2626' : '#000'
                  }}>
                    {formatCurrency(calculateAdjustedSavingsValue(record))}
                  </td>
                  <td style={{ 
                    textAlign: 'center', 
                    padding: '6px',
                    backgroundColor: record.adjusted_savings_percent > 0 ? '#f0fdf4' : record.adjusted_savings_percent < 0 ? '#fef2f2' : '#fff',
                    color: record.adjusted_savings_percent > 0 ? '#166534' : record.adjusted_savings_percent < 0 ? '#dc2626' : '#000'
                  }}>
                    {formatPercentage(record.adjusted_savings_percent)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '6px' }}>
                    {formatMonths(record.break_even_months_exact)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px' }}>
                    {formatCurrency(calculateOneYearSavings(record))}
                  </td>
                  <td style={{ textAlign: 'center', padding: '6px', fontWeight: '600', color: calculateNYearROI(record, 1) > 0 ? '#166534' : calculateNYearROI(record, 1) < 0 ? '#dc2626' : '#000' }}>
                    {formatROIPercentage(calculateNYearROI(record, 1))}
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px' }}>
                    {formatCurrency(calculateTwoYearSavings(record))}
                  </td>
                  <td style={{ textAlign: 'center', padding: '6px', fontWeight: '600', color: calculateNYearROI(record, 2) > 0 ? '#166534' : calculateNYearROI(record, 2) < 0 ? '#dc2626' : '#000' }}>
                    {formatROIPercentage(calculateNYearROI(record, 2))}
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px' }}>
                    {formatCurrency(calculateThreeYearSavings(record))}
                  </td>
                  <td style={{ textAlign: 'center', padding: '6px', fontWeight: '600', color: calculateNYearROI(record, 3) > 0 ? '#166534' : calculateNYearROI(record, 3) < 0 ? '#dc2626' : '#000' }}>
                    {formatROIPercentage(calculateNYearROI(record, 3))}
                  </td>
                  <td style={{ textAlign: 'center', padding: '6px' }}>
                    {!approvedApplications.has(record.application_id) && (
                      <button
                        onClick={() => {

                          recalculateRecord(record);
                        }}
                        style={{
                          padding: '2px 6px',
                          fontSize: '10px',
                          border: '1px solid #007acc',
                          borderRadius: '3px',
                          backgroundColor: '#f8f9fa',
                          cursor: 'pointer',
                          color: '#007acc'
                        }}
                        title="Recalculate all values for this application"
                      >
                        ♻
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {filteredRecords.length > 0 && (
              <tfoot>
                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                  <td style={{
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#f8f9fa',
                    borderRight: '2px solid #dee2e6',
                    padding: '8px',
                    textAlign: 'left',
                    zIndex: 5,
                    minWidth: '150px'
                  }}>
                    <strong>TOTALS</strong>
                  </td>
                  <td style={{
                    position: 'sticky',
                    left: '150px',
                    backgroundColor: '#f8f9fa',
                    borderRight: '2px solid #dee2e6',
                    padding: '8px',
                    textAlign: 'left',
                    zIndex: 4,
                    minWidth: '150px'
                  }}>
                    <strong>({filteredRecords.length} apps)</strong>
                  </td>
                  <td style={{
                    position: 'sticky',
                    left: '300px',
                    backgroundColor: '#f8f9fa',
                    borderRight: '2px solid #dee2e6',
                    padding: '8px',
                    textAlign: 'center',
                    zIndex: 3
                  }}>
                    <strong>-</strong>
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseNum(r.total_annual_cost_current), 0))}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseNum(r.total_disposition_cost), 0))}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseNum(r.future_run_rate_annual), 0))}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseNum(r.planned_project_cost), 0))}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', backgroundColor: '#f0fdf4', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseNum(r.net_savings_value), 0))}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f0fdf4', whiteSpace: 'nowrap' }}>
                    {(() => { const totalTCO = filteredRecords.reduce((s, r) => s + parseNum(r.total_annual_cost_current), 0); const totalNet = filteredRecords.reduce((s, r) => s + parseNum(r.net_savings_value), 0); return `Portfolio: ${formatPercentage(totalTCO > 0 ? totalNet / totalTCO : 0)}`; })()}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.reduce((sum, r) => sum + parseNum(r.lease_buyback_benefit), 0))}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', backgroundColor: '#fff3e0', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.filter(r => r.license_renegotiation).reduce((sum, r) => sum + parseNum(r.renegotiated_license_cost), 0))}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', backgroundColor: '#fff3e0', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {formatCurrency(filteredRecords.filter(r => r.license_renegotiation).reduce((sum, r) => sum + parseNum(r.license_savings), 0))}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', backgroundColor: '#f0fdf4', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.reduce((sum, r) => sum + calculateAdjustedSavingsValue(r), 0))}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px', backgroundColor: '#f0fdf4', whiteSpace: 'nowrap' }}>
                    Avg: {formatPercentage(filteredRecords.reduce((sum, r) => sum + parseNum(r.adjusted_savings_percent), 0) / filteredRecords.length)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>
                    {displayStats.avgBreakEvenMonths > 0 ? `${displayStats.avgBreakEvenMonths.toFixed(1)} months` : 'N/A'}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.reduce((sum, r) => sum + calculateOneYearSavings(r), 0))}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>
                    {(() => { const totalInv = filteredRecords.reduce((s, r) => s + parseNum(r.total_disposition_cost) + parseNum(r.planned_project_cost), 0); const totalTCO = filteredRecords.reduce((s, r) => s + parseNum(r.total_annual_cost_current), 0); const total1y = filteredRecords.reduce((s, r) => s + calculateOneYearSavings(r), 0); const denom = totalInv > 0 ? totalInv : totalTCO; return `Portfolio ROI: ${formatPortfolioROI(denom > 0 ? total1y / denom : 0)}`; })()}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.reduce((sum, r) => sum + calculateTwoYearSavings(r), 0))}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>
                    {(() => { const totalInv = filteredRecords.reduce((s, r) => s + parseNum(r.total_disposition_cost) + parseNum(r.planned_project_cost), 0); const totalTCO = filteredRecords.reduce((s, r) => s + parseNum(r.total_annual_cost_current), 0); const total2y = filteredRecords.reduce((s, r) => s + calculateTwoYearSavings(r), 0); const denom = totalInv > 0 ? totalInv : totalTCO; return `Portfolio ROI: ${formatPortfolioROI(denom > 0 ? total2y / denom : 0)}`; })()}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', whiteSpace: 'nowrap' }}>
                    {formatCurrency(filteredRecords.reduce((sum, r) => sum + calculateThreeYearSavings(r), 0))}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>
                    {(() => { const totalInv = filteredRecords.reduce((s, r) => s + parseNum(r.total_disposition_cost) + parseNum(r.planned_project_cost), 0); const totalTCO = filteredRecords.reduce((s, r) => s + parseNum(r.total_annual_cost_current), 0); const total3y = filteredRecords.reduce((s, r) => s + calculateThreeYearSavings(r), 0); const denom = totalInv > 0 ? totalInv : totalTCO; return `Portfolio ROI: ${formatPortfolioROI(denom > 0 ? total3y / denom : 0)}`; })()}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px' }}>
                    -
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            {!filterGroupId ? (
              <p style={{ fontSize: '16px' }}>Please select a Grouping ID to view benefit realization data.</p>
            ) : records.length === 0 ? (
              <>
                <p>No benefit realization records found.</p>
                <button
                  onClick={initializeFromDispositionAnalysis}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#007acc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Initialize from Disposition Analysis
                </button>
              </>
            ) : (
              <p>No records match the selected filters.</p>
            )}
          </div>
        )}
      </div>}

      {/* Cost Definitions Note */}
      {records.length > 0 && (
        <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '10.5px', color: '#6b7280', lineHeight: '1.5' }}>
          <div style={{ fontWeight: 600, marginBottom: '4px', color: '#4b5563', fontSize: '11px' }}>Cost Definitions</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10.5px' }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top', width: '160px' }}>TCO (Total Annual Cost)</td>
                <td style={{ padding: '3px 8px', verticalAlign: 'top' }}>Total Annual Cost — what you pay today to run/host the application — data center lease, hardware maintenance, on-prem licenses, DBA salaries, power/cooling. Sourced from Costing Analysis / Current Run Rate data (read-only).</td>
                <td style={{ padding: '3px 0 3px 8px', whiteSpace: 'nowrap', verticalAlign: 'top', fontStyle: 'italic', textAlign: 'right' }}>Recurring (Annual)</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>DC (Disposition Cost)</td>
                <td style={{ padding: '3px 8px', verticalAlign: 'top' }}>The migration/disposition execution itself — data migration, code refactoring, testing, cutover downtime costs. Sourced from Disposition Cost Analysis (read-only).</td>
                <td style={{ padding: '3px 0 3px 8px', whiteSpace: 'nowrap', verticalAlign: 'top', fontStyle: 'italic', textAlign: 'right' }}>One-Time</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>FRC (Future Run Rate)</td>
                <td style={{ padding: '3px 8px', verticalAlign: 'top' }}>What you will pay every year going forward in the new environment — cloud instances, managed services, cloud licenses, support contracts</td>
                <td style={{ padding: '3px 0 3px 8px', whiteSpace: 'nowrap', verticalAlign: 'top', fontStyle: 'italic', textAlign: 'right' }}>Recurring (Annual)</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 8px 3px 0', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>PPC (Planned Project Cost)</td>
                <td style={{ padding: '3px 8px', verticalAlign: 'top' }}>One-time investments around the migration that are not the migration itself — new monitoring tools, staff retraining, security audit/certification, cloud architect hiring, reserved instance purchases. For Retire disposition: estimated cost for a successor application that will take over the function or data</td>
                <td style={{ padding: '3px 0 3px 8px', whiteSpace: 'nowrap', verticalAlign: 'top', fontStyle: 'italic', textAlign: 'right' }}>One-Time</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      </div>{/* End scrollable-data-section */}

      {/* Tooltip for Future Run Rate Costs - Rendered outside table */}
      {showTooltip && (
        <div style={{
          position: 'fixed',
          top: '200px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'white',
          color: 'black',
          padding: '12px',
          borderRadius: '6px',
          fontSize: '12px',
          maxWidth: '450px',
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          border: '1px solid #ccc',
          whiteSpace: 'normal'
        }}>
          <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
            Future Run-Rate:
          </div>
          <div style={{ lineHeight: '1.4', color: '#333' }}>
            Estimated annual cost to run/support rationalized functionality after initiative is complete (e.g. Retire = $0, Re-Platform = additional licenses, support, etc. for rationalizing app).
          </div>
        </div>
      )}

      {/* Tooltip for Planned Project Costs - Rendered outside table */}
      {showPlannedProjectTooltip && (
        <div style={{
          position: 'fixed',
          top: '200px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'white',
          color: 'black',
          padding: '12px',
          borderRadius: '6px',
          fontSize: '12px',
          maxWidth: '450px',
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          border: '1px solid #ccc',
          whiteSpace: 'normal'
        }}>
          <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
            Planned Project Cost (One-Time):
          </div>
          <div style={{ lineHeight: '1.6', color: '#333' }}>
            One-time costs incurred to keep application viable if it were to be Retained, Re-Platformed, or Re-Designed.
            <br /><br />
            For applications in <strong>Retire</strong> disposition: this cost may represent the estimated investment required for a successor application that will take over the function or data from the retiring application. At the time of disposition, the successor app may not yet be identified, but the estimated transition cost can be captured here.
          </div>
        </div>
      )}

    </div>
  );
};

export default BenefitRealization;